import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  trackCalculatorFaqExpand,
  trackCalculatorFaqView,
  trackCalculatorRelatedLinkClick,
  trackLinkClick,
  withStoredUtms,
} from '../lib/analytics';

export default function CalculatorQandA({
  title = 'Common questions',
  introduction,
  questions = [],
  relatedLinks = [],
  calculatorId,
  calculatorName,
  location = 'calculator_intro',
  className = '',
}) {
  const sectionRef = useRef(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const trackView = () => trackCalculatorFaqView({
      calculator_id: calculatorId,
      calculator_name: calculatorName,
      cta_location: location,
    });
    if (typeof IntersectionObserver === 'undefined') {
      trackView();
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        trackView();
        observer.disconnect();
      }
    }, { threshold: 0.25 });
    observer.observe(section);
    return () => observer.disconnect();
  }, [calculatorId, calculatorName, location]);

  const baseParams = {
    calculator_id: calculatorId,
    calculator_name: calculatorName,
    cta_location: location,
  };

  return (
    <section ref={sectionRef} className={`pete-card calculator-q-and-a ${className}`.trim()}>
      <h2>{title}</h2>
      {introduction ? <p className="calculator-q-and-a-intro">{introduction}</p> : null}
      <div className="calculator-q-and-a-list">
        {questions.map((item) => (
          <details
            key={item.id}
            className="calculator-q-and-a-item"
            onToggle={(event) => {
              if (event.currentTarget.open) {
                trackCalculatorFaqExpand({ ...baseParams, faq_id: item.id });
              }
            }}
          >
            <summary>{item.question}</summary>
            <div className="calculator-q-and-a-answer">{item.answer}</div>
          </details>
        ))}
      </div>
      {relatedLinks.length > 0 ? (
        <div className="calculator-q-and-a-related" aria-label="Related resources">
          <strong>Related:</strong>{' '}
          {relatedLinks.map((item, index) => {
            const to = withStoredUtms(item.to);
            const payload = { ...baseParams, link_text: item.label, link_url: to };
            return (
              <React.Fragment key={item.id || item.to}>
                {index > 0 ? ' · ' : ''}
                <Link
                  to={to}
                  onClick={() => {
                    trackLinkClick(to, item.label, payload);
                    trackCalculatorRelatedLinkClick(payload);
                  }}
                >
                  {item.label}
                </Link>
              </React.Fragment>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}
